/**
 * useSwipeNavigation
 * Touch swipe composable for navigating between sub-departments in the catalog.
 *
 * Provides follow-finger animation, slide transitions, bounce at boundaries,
 * and cross-department navigation. Touch-only (no mouse/desktop).
 *
 * iOS Safari: uses passive:false listeners + touch-action:pan-y on container.
 */
import { ref, computed, watch, onMounted, onUnmounted, unref } from 'vue'

// ── Constants ──────────────────────────────────────────────────
const SWIPE_THRESHOLD_PX = 80 // Minimum distance for a successful swipe
const VELOCITY_THRESHOLD = 0.3 // px/ms — fast flick overrides distance
const DIRECTION_LOCK_PX = 10 // px before we decide horiz vs vert
const RUBBER_BAND_FACTOR = 0.3 // Reduced response at boundaries
const SLIDE_DURATION = 200 // ms for slide-out / slide-in
const BOUNCE_DURATION = 400 // ms for boundary bounce
const SNAP_BACK_DURATION = 150 // ms for returning when below threshold
const INTERACTIVE_SELECTOR = 'button, input, textarea, select, a, [data-no-swipe]'

/**
 * Build a flat ordered list of all sub-departments across departments.
 * Skips the promotions department (it has no subs).
 */
function buildFlatList(departments, getSubsForDept, promoDeptId) {
  const flat = []
  for (const dept of departments) {
    if (dept.id === promoDeptId) continue
    const subs = getSubsForDept(dept.id)
    for (const sub of subs) {
      flat.push({
        subId: sub.id,
        subName: sub.name,
        deptId: dept.id,
        deptName: dept.name,
      })
    }
  }
  return flat
}

/**
 * Find the current position in the flat list.
 * Returns: integer index if exact match, -1 if promo / before list,
 * or the index of the first sub of the current dept when no sub is selected.
 */
function findIndex(flatList, selectedDeptId, selectedSubId, promoDeptId) {
  if (!selectedDeptId || selectedDeptId === promoDeptId) return -1

  if (selectedSubId) {
    const idx = flatList.findIndex((s) => s.subId === selectedSubId && s.deptId === selectedDeptId)
    if (idx >= 0) return idx
  }

  // Department selected but no sub — treat as "before first sub of this dept"
  const firstOfDept = flatList.findIndex((s) => s.deptId === selectedDeptId)
  return firstOfDept >= 0 ? firstOfDept : -1
}

export function useSwipeNavigation({
  containerRef,
  departments,
  getSubsForDept,
  selectedDepartmentId,
  selectedSubDepartmentId,
  promoDepartmentId,
  onNavigate,
  enabled,
  loadAllSubs,
}) {
  // ── Reactive state ──────────────────────────────────────────
  const swipeOffset = ref(0)
  const swipeTransition = ref('none')
  const isSwiping = ref(false)
  const isAnimating = ref(false)
  const transitionLabel = ref(null) // { deptName, subName } during slide transition

  // ── Touch tracking (non-reactive, internal) ─────────────────
  let startX = 0
  let startY = 0
  let startTime = 0
  let directionLocked = false // Once set, stays for the gesture
  let isHorizontal = false // true = we handle it, false = native scroll
  let currentDeltaX = 0
  let atBoundary = false // true when swiping towards a boundary

  // ── Computed: flat sub-department list ──────────────────────
  const flatSubList = computed(() => {
    const deps = unref(departments) || []
    return buildFlatList(deps, getSubsForDept, promoDepartmentId)
  })

  // ── Navigation targets ──────────────────────────────────────
  function getTarget(direction) {
    const list = flatSubList.value
    if (list.length === 0) return null

    const currentIdx = findIndex(
      list,
      unref(selectedDepartmentId),
      unref(selectedSubDepartmentId),
      promoDepartmentId
    )

    if (direction === 'next') {
      const nextIdx = currentIdx + 1
      return nextIdx < list.length ? list[nextIdx] : null
    } else {
      const prevIdx = currentIdx - 1
      return prevIdx >= 0 ? list[prevIdx] : null
    }
  }

  // ── Swipe style for template binding ────────────────────────
  const swipeStyle = computed(() => ({
    transform: `translate3d(${swipeOffset.value}px, 0, 0)`,
    transition: swipeTransition.value,
  }))

  // ── Animation helpers ───────────────────────────────────────
  function animateTo(targetOffset, duration, easing = 'ease-out') {
    return new Promise((resolve) => {
      swipeTransition.value = `transform ${duration}ms ${easing}`
      swipeOffset.value = targetOffset
      setTimeout(resolve, duration)
    })
  }

  function snapInstant(offset) {
    swipeTransition.value = 'none'
    swipeOffset.value = offset
  }

  async function performSlideNavigation(direction, target) {
    isAnimating.value = true
    transitionLabel.value = { deptName: target.deptName, subName: target.subName, direction }
    const container = unref(containerRef)
    const width = container ? container.offsetWidth : window.innerWidth

    // Phase 1: slide current content off-screen
    const slideOutX = direction === 'next' ? width : -width
    await animateTo(slideOutX, SLIDE_DURATION)

    // Phase 2: call navigation callback (Vue re-renders with new items)
    onNavigate(target.subId, target.subName, target.deptId, target.deptName)

    // Phase 3: snap new content to opposite side (instant, no transition)
    snapInstant(direction === 'next' ? -width : width)

    // Phase 4: one frame later, slide new content into view
    await new Promise((r) => window.requestAnimationFrame(r))
    transitionLabel.value = null
    await animateTo(0, SLIDE_DURATION)

    swipeTransition.value = 'none'
    isAnimating.value = false
  }

  async function performBounce(direction) {
    isAnimating.value = true
    const bounceX = direction === 'next' ? 30 : -30
    await animateTo(bounceX, BOUNCE_DURATION * 0.4, 'ease-out')
    await animateTo(0, BOUNCE_DURATION * 0.6, 'cubic-bezier(0.34, 1.56, 0.64, 1)')
    swipeTransition.value = 'none'
    isAnimating.value = false
  }

  async function performSnapBack() {
    isAnimating.value = true
    await animateTo(0, SNAP_BACK_DURATION)
    swipeTransition.value = 'none'
    isAnimating.value = false
  }

  // ── Touch handlers ──────────────────────────────────────────
  function onTouchStart(e) {
    if (!unref(enabled) || isAnimating.value) return
    if (e.touches.length !== 1) return

    // Ignore touches on interactive elements
    if (e.target.closest(INTERACTIVE_SELECTOR)) return

    startX = e.touches[0].clientX
    startY = e.touches[0].clientY
    startTime = Date.now()
    directionLocked = false
    isHorizontal = false
    currentDeltaX = 0
    atBoundary = false
    isSwiping.value = false
    swipeTransition.value = 'none'
  }

  function onTouchMove(e) {
    if (!unref(enabled) || isAnimating.value) return
    if (e.touches.length !== 1) {
      // Multi-touch: cancel swipe
      if (isSwiping.value) {
        isSwiping.value = false
        performSnapBack()
      }
      return
    }

    const x = e.touches[0].clientX
    const y = e.touches[0].clientY
    const deltaX = x - startX
    const deltaY = y - startY

    // Direction lock: decide once whether this is horizontal or vertical
    if (!directionLocked) {
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)
      if (absX < DIRECTION_LOCK_PX && absY < DIRECTION_LOCK_PX) return // Not enough movement yet
      directionLocked = true
      isHorizontal = absX > absY
    }

    if (!isHorizontal) return // Vertical scroll — let browser handle it

    // Horizontal swipe — we handle it
    e.preventDefault()
    isSwiping.value = true
    currentDeltaX = deltaX

    // Determine if at boundary (no target in swipe direction)
    const direction = deltaX > 0 ? 'next' : 'prev'
    const target = getTarget(direction)
    atBoundary = !target

    // Apply rubber-band at boundaries
    swipeOffset.value = atBoundary ? deltaX * RUBBER_BAND_FACTOR : deltaX
  }

  function onTouchEnd() {
    if (!isSwiping.value) return
    isSwiping.value = false

    const elapsed = Date.now() - startTime
    const absX = Math.abs(currentDeltaX)
    const velocity = elapsed > 0 ? absX / elapsed : 0
    const meetsThreshold = absX >= SWIPE_THRESHOLD_PX || velocity >= VELOCITY_THRESHOLD
    const direction = currentDeltaX > 0 ? 'next' : 'prev'

    if (meetsThreshold) {
      const target = getTarget(direction)
      if (target) {
        performSlideNavigation(direction, target)
      } else {
        performBounce(direction)
      }
    } else {
      performSnapBack()
    }
  }

  function onTouchCancel() {
    if (isSwiping.value) {
      isSwiping.value = false
      performSnapBack()
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────
  function setup() {
    const el = unref(containerRef)
    if (!el) return

    el.addEventListener('touchstart', onTouchStart, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchCancel)

    // Preload all sub-departments for cross-department navigation.
    // Deferred: departments may not be loaded yet (bootstrap is async).
    // The watch below triggers loadAllSubs once departments become available.
    if (loadAllSubs && (unref(departments) || []).length > 0) loadAllSubs()
  }

  function teardown() {
    const el = unref(containerRef)
    if (!el) return

    el.removeEventListener('touchstart', onTouchStart)
    el.removeEventListener('touchmove', onTouchMove)
    el.removeEventListener('touchend', onTouchEnd)
    el.removeEventListener('touchcancel', onTouchCancel)
  }

  // Load all subs once departments become available (after bootstrap)
  if (loadAllSubs) {
    watch(
      () => (unref(departments) || []).length,
      (len) => {
        if (len > 0) loadAllSubs()
      },
      { immediate: true }
    )
  }

  // Auto setup/teardown if containerRef changes (e.g., v-if)
  watch(
    () => unref(containerRef),
    (newEl, oldEl) => {
      if (oldEl) teardown()
      if (newEl) setup()
    }
  )

  onMounted(() => {
    if (unref(containerRef)) setup()
  })

  onUnmounted(() => {
    teardown()
  })

  return {
    swipeStyle,
    isSwiping,
    isAnimating,
    transitionLabel,
  }
}
